<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { computed, reactive, ref } from 'vue';
import { useProjectSettingsModalStore } from '~/stores/project-settings-modal-store';
import AppHeader from '~/components/AppHeader.vue';
import SearchProjects from '~/components/SearchProjects.vue';
import SearchProjectsCollapsible from '~/components/SearchProjectsCollapsible.vue';
import ProjectCatalog from '~/components/project-catalog/ProjectCatalog.vue';
import ProjectSettingsModal from '~/components/project-settings/ProjectSettingsModal.vue';
import ListGridToggle from '~/components/ListGridToggle.vue';
import SortDropdown from '~/components/SortDropdown.vue';
import EmptySearchResults from '~/components/EmptySearchResults.vue';
import ItemsPerPageDropdown from '~/components/ItemsPerPageDropdown.vue';
import EmptyNoneCreated from '~/components/EmptyNoneCreated.vue';
import { usePaginatedProjectList } from '~/composables/paginated-project-list';

useHead({ title: 'Favorites' });

const projectSettingsModalStore = useProjectSettingsModalStore();

const state = reactive({
  isSearchOpen: false,
});

const searchQuery = ref('');

const projectPagination = reactive(
  usePaginatedProjectList({
    scope: 'favorites',
    q: searchQuery,
  }),
);

const isLoading = computed(() => projectPagination.projectList === null);

async function fetchFavorites() {
  await projectPagination.refetch();
}

function onTagClick(tag: string) {
  state.isSearchOpen = true;
  searchQuery.value = searchQuery.value === tag ? '' : tag;
}
</script>

<template>
  <div
    class="container-spacing border-b border-new-gray-100/80 bg-white dark:border-new-gray-950/80 dark:bg-new-slate-700"
  >
    <AppHeader class="flex items-center !gap-0">
      <template #leftColumn>
        <div class="relative flex w-full items-center">
          <h1 class="text-lg font-semibold">{{ $t('favorites.page_heading') }}</h1>

          <!-- Visible on mobile only. -->
          <div class="ml-auto flex h-8 items-center md:hidden">
            <SearchProjectsCollapsible v-model="searchQuery" v-model:is-open="state.isSearchOpen" />
          </div>
        </div>
      </template>

      <template #centerColumn>
        <!-- Visible on desktop only. -->
        <SearchProjects v-model="searchQuery" class="hidden md:block md:w-80" />
      </template>
    </AppHeader>
  </div>

  <div class="container-spacing flex-auto overflow-auto pb-4 pt-3">
    <div class="mb-3 flex items-center">
      <div class="ml-auto flex">
        <ListGridToggle />
        <SortDropdown />
        <ItemsPerPageDropdown class="ml-1.5" />
      </div>
    </div>

    <template v-if="!isLoading">
      <!-- No Favorites -->
      <EmptyNoneCreated
        v-if="projectPagination.hasNoProjects"
        class="mt-10"
        i18n-key="states.noun.favorites"
        :description="$t('favorites.no_favorites.description')"
      />

      <!-- No search results -->
      <EmptySearchResults
        v-else-if="searchQuery && !projectPagination.projectList?.length"
        i18n-key="states.noun.favorites"
      />

      <!-- Users's projects -->
      <ProjectCatalog
        v-else-if="projectPagination.projectList && projectPagination.pagination"
        :project-list="projectPagination.projectList"
        :pagination="projectPagination.pagination"
        :set-page="projectPagination.setPage"
        :tag-click-handler="onTagClick"
        :refetch="fetchFavorites"
      />
    </template>
  </div>

  <ProjectSettingsModal
    v-if="projectSettingsModalStore.project"
    @project-deleted="
      () => {
        projectSettingsModalStore.close();
        fetchFavorites();
      }
    "
    @project-updated="fetchFavorites"
    @project-auth-updated="projectSettingsModalStore.$patch({ project: $event })"
  />
</template>

<style scoped lang="postcss"></style>
